import { Edit, Check } from '@mui/icons-material';
import { Stack, Typography, TextField, IconButton, Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    setCallerProfileId,
    setVoiceCallContent,
    setSendName,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import { CampaignChannels } from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import { VoiceCallerProfile, VoiceContent } from 'domains/content/types';
import FormLayout from 'domains/core/components/FormLayout';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import AutocompleteVoiceCallContent from 'domains/core/components/AutocompleteVoiceCallContent';
import useVoiceCallerProfiles from 'hooks/queries/useVoiceCallerProfile';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import useVoiceContentList from 'hooks/queries/useVoiceContentList';
import useQueryParams from 'hooks/useQueryParams';
import { formatPhoneNumber } from 'utils';

const GUIDING_TEXT =
    'Focus on the goal of the customer interaction and choose content from the dropdown that supports your objective.';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const VoiceCallContentStep = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    // Skip send name updates when editing existing draft/paused curations
    const isEditingExistingDraftOrPaused = status === CampaignStatus.DRAFT || status === CampaignStatus.PAUSED;

    // Edit sendname feature is disabled for non-A/B testing flows
    const editSendnameEnabled = false;
    const currentSend = campaignState.sends[activeSendIndex];
    const currentSendIsVoice = currentSend?.channel === CampaignChannels.VOICE_CALL;
    const { callerProfileId, content, name: sendName } = currentSendIsVoice && currentSend;

    const [voiceContentInputValue, setVoiceContentInputValue] = useState('');
    const [selectedVoiceContentOption, setSelectedVoiceContentOption] = useState<AutocompleteOption<VoiceContent>>();
    const [isContentUnavailable, setIsContentUnavailable] = useState(false);

    // State for editable send name
    const [isEditingName, setIsEditingName] = useState(false);
    const curationName = campaignState.name || '';
    const contentName = content?.name || '';

    // Generate the default name with format: {curationName} - {contentName}.{uuid}
    const generateDefaultName = (cntName?: string) => {
        const effectiveContentName = cntName ?? contentName;
        const uuid = uuidv4();
        const curationPart = curationName || '';
        const contentPart = effectiveContentName || '';

        return `${curationPart} - ${contentPart}.${uuid}`;
    };

    // Get the display name: use sendName from campaignState if available, otherwise use default
    const getDisplayName = () => {
        if (sendName && sendName.trim() !== '') {
            return sendName;
        }
        return generateDefaultName();
    };
    const [customName, setCustomName] = useState<string>(getDisplayName());

    // Validation: Check if name is duplicate
    const isDuplicateName = (() => {
        if (customName.trim() === '') return false;
        // Check if any other send has the same name
        return campaignState.sends.some((send, index) => index !== activeSendIndex && send.name === customName);
    })();
    const hasNameError = isDuplicateName;
    const nameErrorMessage = isDuplicateName ? 'All send names must be different' : '';

    const { isLoading: isCallerProfilesLoading, data: callerProfiles } = useVoiceCallerProfiles({ type: 'voice' });
    const { data: voiceContent } = useVoiceContentList();

    // Sync local state with campaignState when sendName changes
    useEffect(() => {
        setCustomName(getDisplayName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendName, activeSendIndex]);

    // Initialize send name in campaignState if it's empty
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (isEditingExistingDraftOrPaused) return;

        if (!sendName || sendName.trim() === '') {
            const defaultName = generateDefaultName();
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, curationName, contentName, isEditingExistingDraftOrPaused]);

    useEffect(() => {
        if (!currentSendIsVoice) return;

        // Voice Call curations don't support delayed sends yet, but we're leaving this here for when it does
        if (activeSendIndex > 0) {
            setNextStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        } else {
            setNextStep(CustomCampaignSteps.SCHEDULE_CURATION);
        }
        // The dependency array is empty because this useEffect should run exactly once after the initial rendering of the component.
        // The hook is not dependent on any props or state variables to re-run. This is intended to perform the initial setup based on
        // the `activeSendIndex` value when the component mounts without reacting to any further updates or re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disable continue if there's a name error
        if (hasNameError) {
            setIsContinueEnabled(false);
            return;
        }
        const isEnabled = !!content.id && !!callerProfileId;
        setIsContinueEnabled(isEnabled);
    }, [content, callerProfileId, setIsContinueEnabled, hasNameError]);

    useEffect(() => {
        if (content.id && voiceContent) {
            const selectedVoiceContent = voiceContent.find(
                (voiceContentItem: VoiceContent) => voiceContentItem.id === content.id
            );

            // Only proceed if we found the voice content item
            if (selectedVoiceContent) {
                const voiceContentOption = { index: 1, label: selectedVoiceContent.name, value: selectedVoiceContent };

                setSelectedVoiceContentOption(voiceContentOption);
                setVoiceContentInputValue(voiceContentOption.label);
                setIsContentUnavailable(false);
                onContentUnavailableChange?.(false, null);
            } else {
                // Content ID exists but not found in list - content is unavailable (unpublished)
                setSelectedVoiceContentOption(null);
                setVoiceContentInputValue('');
                setIsContentUnavailable(true);
                onContentUnavailableChange?.(true, content.id);
            }
        } else if (!content.id) {
            setIsContentUnavailable(false);
            onContentUnavailableChange?.(false, null);
        }
    }, [content, voiceContent, onContentUnavailableChange]);

    const handleContentSelection = (e: any, option: AutocompleteOption<any>) => {
        if (option) {
            dispatchCampaignAction(
                setVoiceCallContent({ contentId: option.value.id, contentName: option.label }, activeSendIndex)
            );

            // Reset send name to default with new content name (only for new curations)
            if (!isEditingExistingDraftOrPaused) {
                const newContentName = option.label || '';
                const newDefaultName = `${curationName} - ${newContentName}.${uuidv4()}`;
                dispatchCampaignAction(setSendName(newDefaultName, activeSendIndex));
                setCustomName(newDefaultName);
            }

            if (contentPreviewId) {
                setContentPreviewId(option.value.id);
            }
        } else {
            dispatchCampaignAction(setVoiceCallContent({ contentId: null, contentName: '' }, activeSendIndex));
            setSelectedVoiceContentOption(null);
            setContentPreviewId(null);

            // Reset send name to default without content name (only for new curations)
            if (!isEditingExistingDraftOrPaused) {
                const defaultName = `${curationName} - .${uuidv4()}`;
                dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
                setCustomName(defaultName);
            }
        }
    };

    const handlePreviewClick = useCallback(() => {
        setContentPreviewId(!!contentPreviewId ? null : content.id);
    }, [contentPreviewId, setContentPreviewId, content.id]);

    // Handlers for editing send name
    const handleEditClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomName(event.target.value);
    };

    const handleNameSave = () => {
        // Don't save if name is duplicate
        if (hasNameError) {
            return;
        }

        setIsEditingName(false);
        // Dispatch action to save customName to campaignState
        dispatchCampaignAction(setSendName(customName, activeSendIndex));
    };

    const handleNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNameSave();
        }
    };

    if (!currentSendIsVoice) return;

    const StepIcon = channelIcons[CampaignChannels.VOICE_CALL];

    return (
        <FormLayout header="select content" icon={<StepIcon />} guidingText={GUIDING_TEXT} stepName="voice call">
            <Stack rowGap={4}>
                {/* Editable send name section - only shown when feature flag is enabled */}
                {editSendnameEnabled && (
                    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {isEditingName ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <TextField
                                            value={customName}
                                            onChange={handleNameChange}
                                            onKeyPress={handleNameKeyPress}
                                            onBlur={handleNameSave}
                                            autoFocus
                                            multiline
                                            variant="standard"
                                            error={hasNameError}
                                            helperText={nameErrorMessage}
                                            sx={{
                                                width: '100%',
                                                '& .MuiInputBase-input': {
                                                    fontSize: '1.25rem',
                                                    fontWeight: 'bold',
                                                    wordBreak: 'break-word',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleNameSave}
                                        disabled={hasNameError}
                                        sx={{ padding: 0.5, mt: 0.5, flexShrink: 0 }}
                                    >
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {customName}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={handleEditClick}
                                            sx={{ padding: 0.5, flexShrink: 0 }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    {hasNameError && (
                                        <Typography variant="caption" color="error" fontSize={12}>
                                            {nameErrorMessage}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
                <AutocompleteVoiceCallContent
                    isPreviewVisible={!!contentPreviewId}
                    onChange={handleContentSelection}
                    onVoicePreviewClick={handlePreviewClick}
                    onInputChange={(_e, inputValue) => setVoiceContentInputValue(inputValue)}
                    voiceContentInputValue={voiceContentInputValue}
                    value={selectedVoiceContentOption ?? null}
                    isContentUnavailable={isContentUnavailable}
                />
                <Select
                    fullWidth
                    label={
                        <Box
                            component="span"
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                overflow: 'visible',
                                pointerEvents: 'auto',
                                display: 'inline-block',
                            }}
                        >
                            <InputLabel label="Caller ID" labelIconTooltipText="Who is making the voice call?" />
                        </Box>
                    }
                    menuItemStyles={{ textTransform: 'none' }}
                    onChange={(e: any) => dispatchCampaignAction(setCallerProfileId(e.target.value, activeSendIndex))}
                    required
                    value={callerProfileId ?? ''}
                    values={
                        !isCallerProfilesLoading
                            ? callerProfiles?.map((callerProfile: VoiceCallerProfile) => ({
                                  value: callerProfile.id,
                                  label:
                                      `${callerProfile.name} <${callerProfile.countryCode} ` +
                                      formatPhoneNumber(callerProfile.number) +
                                      `>`,
                              }))
                            : []
                    }
                />
            </Stack>
        </FormLayout>
    );
};

export default VoiceCallContentStep;
